import React, { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { loadingToggleAction } from '../../store/actions/AuthActions';
import logo from '../../assets/images/logo/logo-full.png';
import { sendVerificationOTPs, verifyOTPs, registerUser } from '../../api/api';
import Swal from 'sweetalert2';

function Register(props) {
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		mobileNumber: '',
		gender: 'male',
		password: '',
		confirmPassword: '',
		emailOTP: '',
		mobileOTP: '',
		referedBy: '',
		agentId: ''
	});

	const [errors, setErrors] = useState({});
	const [otpSent, setOtpSent] = useState(false);
	const [verificationToken, setVerificationToken] = useState('');
	const [registrationToken, setRegistrationToken] = useState('');
	const [step, setStep] = useState(1); // 1: Initial, 2: OTP Verification, 3: Registration
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const handleChange = (e) => {
		const { name, value } = e.target;
		
		// For mobile number, only allow numbers
		if (name === 'mobileNumber') {
			// Remove any non-digit characters and limit to 10 digits
			const numbersOnly = value.replace(/\D/g, '').slice(0, 10);
			setFormData({
				...formData,
				[name]: numbersOnly
			});
		} else {
			setFormData({
				...formData,
				[name]: value
			});
		}

		// Clear error when user starts typing
		if (errors[name]) {
			setErrors({
				...errors,
				[name]: ''
			});
		}
	};

	const handleSendOTP = async (e) => {
		e.preventDefault();
		const newErrors = {};
		
		if (!formData.email) newErrors.email = 'Email is Required';
		if (!formData.mobileNumber) newErrors.mobileNumber = 'Mobile Number is Required';

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		dispatch(loadingToggleAction(true));
		try {
			const response = await sendVerificationOTPs({
				email: formData.email,
				mobileNumber: formData.mobileNumber
			});

			if (response.success) {
				setOtpSent(true);
				setStep(2); // Move to OTP verification step
				setVerificationToken(response.data.verificationToken);
				setErrors({
					...errors,
					general: 'OTPs sent successfully. Please check your email and phone.'
				});
			}
		} catch (error) {
			setErrors({
				...errors,
				general: error.response?.data?.message || 'Failed to send OTPs'
			});
		} finally {
			dispatch(loadingToggleAction(false));
		}
	};

	const handleVerifyOTP = async (e) => {
		e.preventDefault();
		const newErrors = {};

		if (!formData.emailOTP) newErrors.emailOTP = 'Email OTP is Required';

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		dispatch(loadingToggleAction(true));
		try {
			const response = await verifyOTPs({
				verificationToken,
				emailOTP: formData.emailOTP,
				mobileOTP: '000000' // Hardcoded mobile OTP
			});

			if (response.success) {
				setStep(3); // Move to registration step
				setRegistrationToken(response.data.registrationToken);
				setErrors({
					...errors,
					general: 'OTP verified successfully. Please complete your registration.'
				});
			}
		} catch (error) {
			setErrors({
				...errors,
				general: error.response?.data?.message || 'OTP verification failed'
			});
		} finally {
			dispatch(loadingToggleAction(false));
		}
	};

	const handleSubmit = async (e) => {
		if (e) e.preventDefault();
		const newErrors = {};
		
		// Validate all required fields
		if (!formData.name) newErrors.name = 'Name is Required';
		if (!formData.password) newErrors.password = 'Password is Required';
		if (!formData.agentId) newErrors.agentId = 'Agent ID is Required';
		if (formData.password !== formData.confirmPassword) {
			newErrors.confirmPassword = 'Passwords do not match';
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		try {
			// Log the data being sent
			console.log('Registration data:', {
				name: formData.name,
				email: formData.email,
				mobileNumber: formData.mobileNumber,
				gender: formData.gender,
				password: formData.password,
				registrationToken,
				referedBy: formData.referedBy,
				agentId: formData.agentId.trim()
			});

			const response = await registerUser({
				name: formData.name,
				email: formData.email,
				mobileNumber: formData.mobileNumber,
				gender: formData.gender,
				password: formData.password,
				registrationToken,
				referedBy: formData.referedBy,
				agentId: formData.agentId.trim() // Ensure agentId is included and trimmed
			});
			
			if (response.success) {
				await Swal.fire({
					icon: 'success',
					title: 'Registration Successful!',
					text: 'Please login to continue',
					showConfirmButton: true,
					timer: 2500
				});

				props.dispatch({
					type: 'REGISTRATION_SUCCESS',
					payload: 'Registration successful! Please login.'
				});
				navigate('/login');
			}
		} catch (error) {
			console.error('Registration error:', error);
			setErrors({
				...errors,
				general: error.response?.data?.message || 'Registration failed'
			});
		} finally {
			dispatch(loadingToggleAction(false));
		}
	};

	const renderStepContent = () => {
		switch (step) {
			case 1: // Initial Step - Email and Mobile
				return (
					<form onSubmit={handleSendOTP}>
						<div className="form-group mb-3">
							<label className="mb-1"><strong>Email</strong></label>
							<input
								type="email"
								className="form-control"
								placeholder="Enter Email"
								name="email"
								value={formData.email}
								onChange={handleChange}
							/>
							{errors.email && <div className="text-danger fs-12">{errors.email}</div>}
						</div>

						<div className="form-group mb-3">
							<label className="mb-1"><strong>Mobile Number</strong></label>
							<input
								type="tel"
								className="form-control"
								placeholder="Enter Mobile Number"
								name="mobileNumber"
								value={formData.mobileNumber}
								onChange={handleChange}
								maxLength="10"
								pattern="[0-9]*"
								inputMode="numeric"
							/>
							{errors.mobileNumber && <div className="text-danger fs-12">{errors.mobileNumber}</div>}
						</div>

						<div className="text-center mt-4">
							<button type="submit" className="btn btn-primary btn-block">
								Send OTP
							</button>
						</div>
					</form>
				);

			case 2: // OTP Verification Step
				return (
					<form onSubmit={handleVerifyOTP}>
						<div className="form-group mb-3">
							<label className="mb-1"><strong>Email OTP</strong></label>
							<input
								type="text"
								className="form-control"
								placeholder="Enter Email OTP"
								name="emailOTP"
								value={formData.emailOTP}
								onChange={handleChange}
							/>
							{errors.emailOTP && <div className="text-danger fs-12">{errors.emailOTP}</div>}
						</div>

						<div className="text-center mt-4">
							<button type="submit" className="btn btn-primary btn-block">
								Verify OTP
							</button>
						</div>
					</form>
				);

			case 3: // Registration Step
				return (
					<form onSubmit={handleSubmit}>
						<div className="form-group mb-3">
							<label className="mb-1"><strong>Full Name</strong></label>
							<input
								type="text"
								className="form-control"
								placeholder="Enter Name"
								name="name"
								value={formData.name}
								onChange={handleChange}
							/>
							{errors.name && <div className="text-danger fs-12">{errors.name}</div>}
						</div>

						<div className="form-group mb-3">
							<label className="mb-1"><strong>Gender</strong></label>
							<select
								className="form-control"
								name="gender"
								value={formData.gender}
								onChange={handleChange}
							>
								<option value="male">Male</option>
								<option value="female">Female</option>
								<option value="other">Other</option>
							</select>
						</div>

						<div className="form-group mb-3">
							<label className="mb-1"><strong>Password</strong></label>
							<input
								type="password"
								className="form-control"
								placeholder="Enter Password"
								name="password"
								value={formData.password}
								onChange={handleChange}
							/>
							{errors.password && <div className="text-danger fs-12">{errors.password}</div>}
						</div>

						<div className="form-group mb-3">
							<label className="mb-1"><strong>Confirm Password</strong></label>
							<input
								type="password"
								className="form-control"
								placeholder="Confirm Password"
								name="confirmPassword"
								value={formData.confirmPassword}
								onChange={handleChange}
							/>
							{errors.confirmPassword && <div className="text-danger fs-12">{errors.confirmPassword}</div>}
						</div>

						<div className="form-group mb-3">
							<label className="mb-1">
								<strong>Agent ID</strong>
								<span className="text-danger">*</span>
							</label>
							<input
								type="text"
								className="form-control"
								placeholder="Enter Agent ID"
								name="agentId"
								value={formData.agentId}
								onChange={handleChange}
								required
							/>
							{errors.agentId && <div className="text-danger fs-12">{errors.agentId}</div>}
						</div>

						<div className="form-group mb-3">
							<label className="mb-1"><strong>Referral Code (Optional)</strong></label>
							<input
								type="text"
								className="form-control"
								placeholder="Enter Referral Code"
								name="referedBy"
								value={formData.referedBy}
								onChange={handleChange}
							/>
						</div>

						<div className="text-center mt-4">
							<button type="submit" className="btn btn-primary btn-block">
								Sign up
							</button>
						</div>
					</form>
				);

			default:
				return null;
		}
	};

	return (
		<div className="authincation h-100 p-meddle">
			<div className="container h-100">
				<div className="row justify-content-center h-100 align-items-center">
					<div className="col-md-6">
						<div className="authincation-content">
							<div className="row no-gutters">
								<div className="col-xl-12">
									<div className="auth-form">
										<div className="text-center mb-3">
											<Link to="/"><img src={logo} alt="" /></Link>
										</div>
										<h4 className="text-center mb-4">
											{step === 1 && "Enter Your Details"}
											{step === 2 && "Verify OTP"}
											{step === 3 && "Complete Registration"}
										</h4>
										
										{errors.general && (
											<div className="alert alert-danger">{errors.general}</div>
										)}
										{props.errorMessage && (
											<div className="alert alert-danger">{props.errorMessage}</div>
										)}
										{props.successMessage && (
											<div className="alert alert-success">{props.successMessage}</div>
										)}

										{renderStepContent()}

										<div className="new-account mt-3">
											<p>Already have an account? <Link className="text-primary" to="/login">Sign in</Link></p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

const mapStateToProps = (state) => {
	return {
		errorMessage: state.auth.errorMessage,
		successMessage: state.auth.successMessage,
		showLoading: state.auth.showLoading,
	};
};

export default connect(mapStateToProps)(Register);

